// React
import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
// Stores && actions
import UserStore from 'stores/UserStore';
import AccountStore from 'stores/AccountStore';
import UserAction from 'actions/UserAction';
import LoggedUserStore from 'stores/LoggedUserStore';
// Material components
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Pagination from '@mui/lab/Pagination';
import IconButton from '@mui/material/IconButton';
import IconRefresh from '@mui/icons-material/Cached';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
// PCP Components
import UserActionsTable from 'components/tables/UserActionsTable/UserActionsTable';
import UserActionsFiltersSelect from 'components/sheets/UserActionsSheet/UserActionsFiltersSelect';
//Style
import useStyles from 'components/page/UserActionPage/UserActionsPage.style';
//Chartjs
import { Bar } from 'react-chartjs-2';
import { createData, graphOptions } from 'components/page/UserActionPage/UserActionsPage.util';
//Locales
import translate from 'locales/i18n';

const params = {
    pageSize: 50,
};

const UserActionsPage = () => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [selectedActions, setSelectedActions] = useState([]);
    const [selectedGranularity, setSelectedGranularity] = useState('DAY');

    const adapter = new AdapterDayjs();
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    const [fromStr, setFromStr] = useState(adapter.date(d));
    const [toStr, setToStr] = useState(adapter.date(new Date()));

    const [isGraphPage, setIsGraphPage] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const classes = useStyles();

    const [graphData, setGraphData] = useState(UserStore.getUserActionsGraphData());
    const [actionRecords, setActionRecords] = useState(UserStore.getUserActionsData() ? UserStore.getUserActionsData().actionRecords : []);
    const [count, setCount] = useState(UserStore.getUserActionsData() ? UserStore.getUserActionsData().count : 0);

    const [pageIndex, setPageIndex] = useState(1);
    const [request, setRequest] = useState(true);

    useEffect(() => {
        requestData();
    }, [pageIndex, request]);

    useEffect(() => {
        UserStore.addChangeListener(onUserStoreChange);
        return () => UserStore.removeChangeListener(onUserStoreChange);
    });

    const onUserStoreChange = () => {
        setGraphData(UserStore.getUserActionsGraphData());
        setIsLoading(UserStore.isLoadingUserActions());
        setActionRecords(UserStore.getUserActionsData() ? UserStore.getUserActionsData().actionRecords : []);
        setCount(UserStore.getUserActionsData() ? UserStore.getUserActionsData().count : 0);
    };

    const requestData = () => {
        const userIds = selectedUsers.map(selUser => UserStore.getUsers().find(user => user.login === selUser).id);
        const accountIds = selectedAccounts.map(selAccount => AccountStore.getAccounts().find(account => account.name === selAccount).id);
        UserAction.getUserActionsGraph({
            fromStr,
            toStr,
            users: userIds,
            actions: selectedActions,
            granularity: selectedGranularity,
        });
        UserAction.getUserActions({
            fromStr,
            toStr,
            pageIndex,
            pageSize: params.pageSize,
            selectedActions,
            selectedAccounts: accountIds,
            selectedUsers: userIds
        });
    };

    const reset = () => {
        setSelectedAccounts([]);
        setSelectedUsers([]);
        setSelectedGranularity('DAY');
        setSelectedActions([]);
        setFromStr(adapter.date(d));
        setToStr(adapter.date(new Date()));
        setRequest(!request);
    };

    const handleChangePage = (value) => setPageIndex(value);

    const renderGraphPage = () => <div className={classes.graphPageContainer}>
        <Paper className={classes.graphContainer}>
            <Bar
                options={graphOptions}
                data={createData(graphData)}
                style={{ maxHeight: '100%' }}
            />
        </Paper>
        <div className={classes.graphFooter}>
            <b>Rappel : </b> Attention, la première et la dernière barre peuvent correspondre à des données partielles.
        </div>
    </div>;

    const renderTablePage = () => <div className={classes.graphPageContainer}>
        <UserActionsTable
            actionRecords={actionRecords} />
        <Pagination count={count > 0 ? Math.ceil(count / params.pageSize) : 0}
            page={pageIndex}
            onChange={(event, value) => handleChangePage(value)}
            style={{ marginTop: '6px' }}
            color='primary'
            variant='outlined'
            shape='rounded' />
    </div>;

    if (!LoggedUserStore.isLogged()) {
        return (
            <Redirect to='/login' />
        );
    }

    return <div className={classes.root}>
        <div className={classes.header}>
            <div className={classes.headerSelectors}>
                <UserActionsFiltersSelect
                    selectedUsers={selectedUsers}
                    setSelectedUsers={setSelectedUsers}
                    selectedAccounts={selectedAccounts}
                    setSelectedAccounts={setSelectedAccounts}
                    selectedActions={selectedActions}
                    setSelectedActions={setSelectedActions}
                    selectedGranularity={selectedGranularity}
                    setSelectedGranularity={setSelectedGranularity}
                    fromStr={fromStr}
                    setFromStr={setFromStr}
                    toStr={toStr}
                    setToStr={setToStr}
                    isGraphPage={isGraphPage}
                    isLoading={isLoading}
                />
            </div>
            <div className={classes.headerActions}>
                <Button onClick={() => requestData()} disabled={isLoading}>
                    {translate.i18n('ANALYZE')}
                </Button>
                <Tooltip title={translate.i18n('DEFAULT_FILTERS')} arrow position='bottom'>
                    <IconButton onClick={() => reset()}>
                        <IconRefresh />
                    </IconButton>
                </Tooltip>
                <Tooltip title={isGraphPage ? translate.i18n('SEE_TABLE') : translate.i18n('SEE_GRAPH')} arrow position='bottom'>
                    <IconButton onClick={() => setIsGraphPage(!isGraphPage)}>
                        {isGraphPage ? <SwitchLeftIcon /> : <SwitchRightIcon />}
                    </IconButton>
                </Tooltip>
            </div>
        </div>
        <Slide direction='right' in={isGraphPage} mountOnEnter unmountOnExit>
            {renderGraphPage()}
        </Slide>
        <Slide direction='left' in={!isGraphPage} mountOnEnter unmountOnExit>
            {renderTablePage()}
        </Slide>
    </div>;
};

export default UserActionsPage;
